import { useCallback, useEffect, useRef, useState } from 'react';
import { collection, doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import type { Project } from '../types';
import { errorReporter } from '@/services/errorReportingService';

interface UseProjectsResult {
  projects: Project[];
  isLoading: boolean;
  isMutating: boolean;
  addProject: (name: string) => Promise<Project | null>;
  removeProject: (projectId: string) => Promise<void>;
  updateProjectTags: (projectId: string, tags: string[]) => Promise<void>;
}

const sanitizeProjects = (rawProjects: unknown): Project[] => {
  if (!Array.isArray(rawProjects)) {
    return [];
  }

  return rawProjects
    .map((project): Project | null => {
      if (!project || typeof project !== 'object') {
        return null;
      }

      const { id, name, tags, createdAt } = project as Partial<Project> & Record<string, unknown>;

      if (!id || typeof id !== 'string' || !name || typeof name !== 'string') {
        return null;
      }

      return {
        id,
        name,
        tags: Array.isArray(tags) ? tags.filter((tag): tag is string => typeof tag === 'string') : [],
        createdAt: typeof createdAt === 'string' ? createdAt : undefined,
      } satisfies Project;
    })
    .filter((project): project is Project => project !== null);
};

const generateProjectId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `project-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const useProjects = (userId?: string | null): UseProjectsResult => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isMutating, setIsMutating] = useState<boolean>(false);
  const projectsRef = useRef<Project[]>([]);

  useEffect(() => {
    if (!userId) {
      setProjects([]);
      projectsRef.current = [];
      setIsLoading(false);
      return undefined;
    }

    setIsLoading(true);

    const docRef = doc(collection(db, 'users'), userId);

    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        const data = snapshot.data();
        const sanitized = sanitizeProjects(data?.projects);
        setProjects(sanitized);
        projectsRef.current = sanitized;
        setIsLoading(false);
      },
      (error) => {
        errorReporter.expected('Failed to subscribe to projects', error, {
          feature: 'projects',
          operation: 'subscribe',
        });
        setProjects([]);
        projectsRef.current = [];
        setIsLoading(false);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [userId]);

  const addProject = useCallback(
    async (name: string) => {
      if (!userId) {
        return null;
      }

      const trimmedName = name.trim();
      if (!trimmedName) {
        return null;
      }

      const existing = projectsRef.current.find((project) => project.name.toLowerCase() === trimmedName.toLowerCase());

      if (existing) {
        return existing;
      }

      const docRef = doc(collection(db, 'users'), userId);
      const newProject: Project = {
        id: generateProjectId(),
        name: trimmedName,
        tags: [],
        createdAt: new Date().toISOString(),
      };

      setIsMutating(true);
      try {
        await setDoc(
          docRef,
          {
            projects: [...projectsRef.current, newProject],
          },
          { merge: true },
        );
        return newProject;
      } finally {
        setIsMutating(false);
      }
    },
    [userId],
  );

  const removeProject = useCallback(
    async (projectId: string) => {
      if (!userId) {
        return;
      }

      const docRef = doc(collection(db, 'users'), userId);
      const filteredProjects = projectsRef.current.filter((project) => project.id !== projectId);

      setIsMutating(true);
      try {
        await setDoc(
          docRef,
          {
            projects: filteredProjects,
          },
          { merge: true },
        );
      } finally {
        setIsMutating(false);
      }
    },
    [userId],
  );

  const updateProjectTags = useCallback(
    async (projectId: string, tags: string[]) => {
      if (!userId) {
        return;
      }

      const docRef = doc(collection(db, 'users'), userId);
      const updatedProjects = projectsRef.current.map((project) =>
        project.id === projectId ? { ...project, tags: [...tags] } : project,
      );

      setIsMutating(true);
      try {
        await setDoc(
          docRef,
          {
            projects: updatedProjects,
          },
          { merge: true },
        );
      } finally {
        setIsMutating(false);
      }
    },
    [userId],
  );

  return {
    projects,
    isLoading,
    isMutating,
    addProject,
    removeProject,
    updateProjectTags,
  };
};
